import { initializeApp } from "firebase/app";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

const firebaseConfig = {
  apiKey: "AIzaSyD3UbJ1SbgMde5K9NPSOml7OvTjkrmeSu8",
  authDomain: "th-v2-c9207.firebaseapp.com",
  projectId: "th-v2-c9207",
  storageBucket: "th-v2-c9207.firebasestorage.app",
  messagingSenderId: "200869126028",
  appId: "1:200869126028:web:d0aa22aada81cb545180a9"
};

const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

// -------------------------------------------------------
// Auth
// -------------------------------------------------------

import {
  getAuth,
  GoogleAuthProvider,
  TwitterAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  deleteUser
} from 'firebase/auth';

const auth = getAuth(app);
const googleProvider   = new GoogleAuthProvider();
const twitterProvider  = new TwitterAuthProvider();
const facebookProvider = new FacebookAuthProvider();

export const signInWithGoogle   = () => signInWithPopup(auth, googleProvider);
export const signInWithTwitter  = () => signInWithPopup(auth, twitterProvider);
export const signInWithFacebook = () => signInWithPopup(auth, facebookProvider);
export const signInWithEmail    = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const signUpWithEmail    = (email, password) => createUserWithEmailAndPassword(auth, email, password);
export const signOutUser        = () => signOut(auth);

export { auth };

// -------------------------------------------------------
// Firestore
// -------------------------------------------------------

import {
  getFirestore,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  writeBatch,
  arrayUnion,
  arrayRemove,
  increment,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore/lite';

const db = getFirestore(app);

// -------------------------------------------------------
// Chasse CRUD
// -------------------------------------------------------

export async function saveChasse(chasseObj) {
  const plain = JSON.parse(JSON.stringify(chasseObj));
  if (plain.chasseId) {
    const ref = doc(db, 'allChasses', plain.chasseId);
    await setDoc(ref, plain, { merge: true });
    return plain.chasseId;
  } else {
    const newRef = doc(collection(db, 'allChasses'));
    await setDoc(newRef, { ...plain, chasseId: newRef.id });
    return newRef.id;
  }
}

export async function deleteChasse(chasseId) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const chasseRef = doc(db, 'allChasses', chasseId);
  const snap      = await getDoc(chasseRef);
  if (!snap.exists()) throw new Error('Chasse not found');

  // Ownership check: compare stored creatorTag against current user's tag
  const userSnap = await getDoc(doc(db, 'users', user.uid));
  const userTag  = userSnap.exists() ? userSnap.data().userTag : null;
  if (!userTag || snap.data().creatorTag !== userTag) throw new Error('Not authorized');

  const steps = snap.data().steps || [];
  await deleteDoc(chasseRef);
  await Promise.all(
    steps
      .filter(s => s.clue?.imageStoragePath)
      .map(s => deleteClueImage(s.clue.imageStoragePath))
  );
}

// Upload a clue image; returns { url, path } on success.
// onProgress(0..100) called during upload.
export async function uploadClueImage(file, onProgress) {
  const uuid = crypto.randomUUID();
  const path = `clues/${uuid}`;
  const fileRef = storageRef(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file);
    task.on(
      'state_changed',
      snap => onProgress && onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, path });
      }
    );
  });
}

export async function deleteClueImage(storagePath) {
  if (!storagePath) return;
  try {
    await deleteObject(storageRef(storage, storagePath));
  } catch (e) { /* already gone — non-critical */ }
}

export async function fetchChasseInfos(chasseId) {
  const docRef  = doc(db, 'allChasses', chasseId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  const { steps, ...chasseInfos } = data;
  chasseInfos.numberOfSteps = steps ? steps.length : 0;
  chasseInfos.firstStepClue = steps && steps.length > 0 ? steps[0].clue : null;
  return chasseInfos;
}

export async function fetchStep(chasseId, stepNum) {
  const docRef  = doc(db, 'allChasses', chasseId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  const data = docSnap.data();
  if (!data.steps) return null;
  return data.steps.find(s => s.number === stepNum) || null;
}

export async function fetchTotalSteps(chasseId) {
  const docRef  = doc(db, 'allChasses', chasseId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return 0;
  return (docSnap.data().steps || []).length;
}

export async function fetchFullChasse(chasseId) {
  const docRef  = doc(db, 'allChasses', chasseId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return docSnap.data();
}

export async function setField(path, docName, fieldName, newValue) {
  const ref = doc(db, path, docName);
  await updateDoc(ref, { [fieldName]: newValue });
}

// -------------------------------------------------------
// Search
// -------------------------------------------------------

export async function searchQuery(queryStr) {
  if (!queryStr) return [];

  // Prefixed queries from Section components
  if (queryStr.startsWith('continue ')) {
    const tag = queryStr.slice(9).trim();
    return await fetchUserSectionByTag('participating', tag);
  }
  if (queryStr.startsWith('made ')) {
    const tag = queryStr.slice(5).trim();
    return await searchByUserTag(tag);
  }
  if (queryStr.startsWith('drafts ')) {
    const tag = queryStr.slice(7).trim();
    return await fetchUserDrafts(tag);
  }
  if (queryStr.startsWith('completed ')) {
    const tag = queryStr.slice(10).trim();
    return await fetchUserSectionByTag('completed', tag);
  }
  if (queryStr.startsWith('saved ')) {
    const tag = queryStr.slice(6).trim();
    return await fetchUserSectionByTag('saved', tag);
  }
  if (queryStr.startsWith('banned ')) {
    const tag = queryStr.slice(7).trim();
    return await fetchUserSectionByTag('banned', tag);
  }
  if (queryStr === 'recent') {
    return await fetchRecentPublicChasses();
  }

  // Generic search: by tag + by creator
  const byTag     = await searchChassesByTag(queryStr);
  const byCreator = await searchByUserTag(queryStr);

  // Merge and deduplicate
  const seen = new Set();
  const results = [];
  for (const item of [...byTag, ...byCreator]) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      results.push(item);
    }
  }
  return results;
}

function toStub(d) {
  const data = d.data();
  return {
    id:              d.id,
    title:           data.title           || '',
    creatorTag:      data.creatorTag      || '',
    tags:            data.tags            || [],
    stepCount:       data.steps ? data.steps.length : 0,
    dateCreated:     data.dateCreated     || null,
    completionCount: data.completionCount || 0,
  };
}

export async function fetchUserDrafts(creatorTag) {
  try {
    const q = query(
      collection(db, 'allChasses'),
      where('creatorTag', '==', creatorTag),
      where('published', '==', false),
      limit(20)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ ...toStub(d), isDraft: true }))
      .sort((a, b) => (b.dateCreated?.seconds ?? 0) - (a.dateCreated?.seconds ?? 0));
  } catch (e) {
    console.error('fetchUserDrafts:', e);
    return [];
  }
}

export async function searchByUserTag(creatorTag) {
  try {
    const q = query(
      collection(db, 'allChasses'),
      where('creatorTag', '==', creatorTag),
      where('published', '==', true),
      where('isPublic', '==', true),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map(toStub)
      .sort((a, b) => (b.dateCreated?.seconds ?? 0) - (a.dateCreated?.seconds ?? 0));
  } catch (e) {
    console.error('searchByUserTag:', e);
    return [];
  }
}

export async function searchChassesByTag(tag) {
  try {
    const q = query(
      collection(db, 'allChasses'),
      where('tags', 'array-contains', tag.toLowerCase().trim()),
      where('published', '==', true),
      where('isPublic', '==', true),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map(toStub);
  } catch (e) {
    console.error('searchChassesByTag:', e);
    return [];
  }
}

export async function fetchRecentPublicChasses(n = 10) {
  try {
    const q = query(
      collection(db, 'allChasses'),
      where('published', '==', true),
      where('isPublic', '==', true),
      orderBy('dateCreated', 'desc'),
      limit(n)
    );
    const snap = await getDocs(q);
    return snap.docs.map(toStub);
  } catch (e) {
    console.error('fetchRecentPublicChasses:', e);
    return [];
  }
}

// Fetches chasse stubs from a user's section array (participating / completed)
async function fetchUserSectionByTag(section, userTag) {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('userTag', '==', userTag));
    const snap = await getDocs(q);
    if (snap.empty) return [];

    const userData = snap.docs[0].data();
    const ids = (userData[section] || []).slice().reverse().slice(0, 20);
    if (ids.length === 0) return [];

    const results = await Promise.all(
      ids.map(async (chasseId) => {
        const docSnap = await getDoc(doc(db, 'allChasses', chasseId));
        if (!docSnap.exists()) return null;
        return toStub(docSnap);
      })
    );
    return results.filter(Boolean);
  } catch (e) {
    console.error('fetchUserSectionByTag:', e);
    return [];
  }
}

// -------------------------------------------------------
// User management
// -------------------------------------------------------

export async function getUserTag() {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef  = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data().userTag;
  }

  // New user — generate a tag from displayName or random
  let baseTag = user.displayName
    ? user.displayName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9_]/g, '').slice(0, 20)
    : '';
  if (baseTag.length < 3) {
    baseTag = 'user' + Math.floor(1000 + Math.random() * 9000);
  }

  // Ensure uniqueness
  let tag = baseTag;
  let suffix = 1;
  while (await tagInDb(tag)) {
    tag = baseTag + suffix;
    suffix++;
  }

  await setDoc(userRef, { userTag: tag, participating: [], completed: [], saved: [], banned: [], unitPref: 'metric' }, { merge: true });
  return tag;
}

export async function tagInDb(tag) {
  try {
    const q = query(collection(db, 'users'), where('userTag', '==', tag));
    const snap = await getDocs(q);
    return !snap.empty;
  } catch (e) {
    console.error('tagInDb:', e);
    throw e;
  }
}

export async function changeCreatorTag(newTag, oldTag) {
  const user = auth.currentUser;
  if (!user) return;

  try {
    // Update user doc
    await updateDoc(doc(db, 'users', user.uid), { userTag: newTag });

    // Batch-update all chasses with old creatorTag
    const q = query(collection(db, 'allChasses'), where('creatorTag', '==', oldTag));
    const snap = await getDocs(q);

    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.update(d.ref, { creatorTag: newTag }));
    await batch.commit();
  } catch (e) {
    console.error('changeCreatorTag:', e);
  }
}

// -------------------------------------------------------
// Participation & Progress
// -------------------------------------------------------

const MILESTONES = new Set([1, 5, 10, 50, 100, 500, 1000])

export async function participateInChasse(chasseId) {
  const user = auth.currentUser;
  if (!user) return;

  const userRef   = doc(db, 'users', user.uid);
  const chasseRef = doc(db, 'allChasses', chasseId);

  await updateDoc(userRef, { participating: arrayUnion(chasseId) });

  try {
    await updateDoc(chasseRef, { participants: increment(1) });
  } catch (e) {
    await setDoc(chasseRef, { participants: 1 }, { merge: true });
  }

  // Check for milestone notification
  const chasseSnap = await getDoc(chasseRef);
  const data = chasseSnap.data();
  if (!data) return;
  const newCount = data.participants || 0;
  if (!MILESTONES.has(newCount)) return;
  const creatorTag = data.creatorTag;
  if (!creatorTag) return;

  const creatorQuery = await getDocs(
    query(collection(db, 'users'), where('userTag', '==', creatorTag), limit(1))
  );
  if (creatorQuery.empty) return;
  const creatorUid = creatorQuery.docs[0].id;

  await addDoc(collection(db, 'users', creatorUid, 'notifications'), {
    type: 'milestone',
    chasseId,
    chasseTitle: data.title || '',
    participantCount: newCount,
    read: false,
    createdAt: serverTimestamp()
  });
}

export async function isParticipating(chasseId) {
  const user = auth.currentUser;
  if (!user) return false;
  const snap = await getDoc(doc(db, 'users', user.uid));
  if (!snap.exists()) return false;
  const data = snap.data();
  return (data.participating || []).includes(chasseId);
}

export async function saveProgress(chasseId, stepNum) {
  const user = auth.currentUser;
  if (!user) return;
  const progressRef = doc(db, 'users', user.uid, 'progress', chasseId);
  await setDoc(progressRef, { stepNum }, { merge: true });
}

export async function getProgress(chasseId) {
  const user = auth.currentUser;
  if (!user) return { stepNum: 1, banned: false };
  const progressRef = doc(db, 'users', user.uid, 'progress', chasseId);
  const snap = await getDoc(progressRef);
  if (!snap.exists()) return { stepNum: 1, banned: false };
  const data = snap.data();
  return { stepNum: data.stepNum ?? 1, banned: data.banned ?? false };
}

export async function resetProgressToStep1(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'progress', chasseId), { stepNum: 1 });
}

export async function banFromChasse(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'progress', chasseId), { stepNum: 1, banned: true });
  await updateDoc(doc(db, 'users', user.uid), {
    participating: arrayRemove(chasseId),
    banned: arrayUnion(chasseId)
  });
}

export async function markComplete(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  const userRef = doc(db, 'users', user.uid);
  await updateDoc(userRef, {
    participating: arrayRemove(chasseId),
    completed:     arrayUnion(chasseId),
  });
  await updateDoc(doc(db, 'allChasses', chasseId), {
    completionCount: increment(1),
  });
}

export async function isCompleted(chasseId) {
  const user = auth.currentUser;
  if (!user) return false;
  const snap = await getDoc(doc(db, 'users', user.uid));
  if (!snap.exists()) return false;
  return (snap.data().completed || []).includes(chasseId);
}

export async function getHasLiked(chasseId) {
  const user = auth.currentUser;
  if (!user) return false;
  const snap = await getDoc(doc(db, 'users', user.uid));
  if (!snap.exists()) return false;
  return (snap.data().likedChasses || []).includes(chasseId);
}

export async function likeChasse(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  await updateDoc(doc(db, 'users', user.uid), { likedChasses: arrayUnion(chasseId) });
  await updateDoc(doc(db, 'allChasses', chasseId), { likeCount: increment(1) });
}

export async function unlikeChasse(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  await updateDoc(doc(db, 'users', user.uid), { likedChasses: arrayRemove(chasseId) });
  await updateDoc(doc(db, 'allChasses', chasseId), { likeCount: increment(-1) });
}

export async function restartChasse(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  const userRef = doc(db, 'users', user.uid);
  await updateDoc(userRef, {
    completed:     arrayRemove(chasseId),
    participating: arrayUnion(chasseId),
  });
  await setDoc(doc(db, 'users', user.uid, 'progress', chasseId), { stepNum: 1 });
}

export async function getUserUnitPref() {
  const user = auth.currentUser;
  if (!user) return 'metric';
  const snap = await getDoc(doc(db, 'users', user.uid));
  return snap.exists() ? (snap.data().unitPref ?? 'metric') : 'metric';
}

export async function setUserUnitPref(pref) {
  const user = auth.currentUser;
  if (!user) return;
  await updateDoc(doc(db, 'users', user.uid), { unitPref: pref });
}

export async function saveBookmark(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  await updateDoc(doc(db, 'users', user.uid), { saved: arrayUnion(chasseId) });
}

export async function removeBookmark(chasseId) {
  const user = auth.currentUser;
  if (!user) return;
  await updateDoc(doc(db, 'users', user.uid), { saved: arrayRemove(chasseId) });
}

export async function isBookmarked(chasseId) {
  const user = auth.currentUser;
  if (!user) return false;
  const snap = await getDoc(doc(db, 'users', user.uid));
  if (!snap.exists()) return false;
  return (snap.data().saved || []).includes(chasseId);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

export async function fetchNearbyChasses(lat, lon, radiusKm = 50) {
  const q = query(
    collection(db, 'allChasses'),
    where('published', '==', true),
    where('isPublic', '==', true),
    orderBy('dateCreated', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  const results = [];
  for (const d of snap.docs) {
    const data = d.data();
    const steps = (data.steps || []).sort((a, b) => a.number - b.number);
    // Primary: explicit geotag with real coords
    let loc = (data.location?.lat != null && data.location?.lon != null)
      ? { lat: data.location.lat, lon: data.location.lon }
      : null;
    // Fallback: first step Location-type clue or solution
    if (!loc) {
      const first = steps[0];
      if (!first) continue;
      loc = (first.clue?.type === 'Location' && first.clue?.location)
        ? first.clue.location
        : (first.solution?.type === 'Location' && first.solution?.location)
          ? first.solution.location
          : null;
    }
    if (!loc) continue;
    if (haversineKm(lat, lon, loc.lat, loc.lon) > radiusKm) continue;
    results.push({
      id: data.chasseId || d.id,
      title: data.title || '',
      creatorTag: data.creatorTag || '',
      tags: data.tags || [],
      stepCount: steps.length,
      completionCount: data.completionCount || 0,
    });
  }
  return results;
}

export async function getNotifications(uid) {
  const q = query(
    collection(db, 'users', uid, 'notifications'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function markNotificationRead(uid, notifId) {
  await updateDoc(doc(db, 'users', uid, 'notifications', notifId), { read: true });
}

export async function deleteAccount() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const userSnap = await getDoc(doc(db, 'users', user.uid));
  if (!userSnap.exists()) throw new Error('User not found');
  const userTag = userSnap.data().userTag;

  // Handle chasses: anonymize published ones, delete drafts
  const chassesSnap = await getDocs(
    query(collection(db, 'allChasses'), where('creatorTag', '==', userTag))
  );
  await Promise.all(chassesSnap.docs.map(async (d) => {
    const data = d.data();
    if (data.published) {
      // Keep published chasses but anonymize the creator tag
      await updateDoc(d.ref, { creatorTag: '[deleted]' });
    } else {
      // Delete drafts and their storage images
      const steps = data.steps || [];
      await Promise.all(
        steps
          .filter(s => s.clue?.imageStoragePath)
          .map(s => deleteClueImage(s.clue.imageStoragePath))
      );
      await deleteDoc(d.ref);
    }
  }));

  // Delete progress subcollection
  const progressSnap = await getDocs(collection(db, 'users', user.uid, 'progress'));
  await Promise.all(progressSnap.docs.map(d => deleteDoc(d.ref)));

  // Delete user doc
  await deleteDoc(doc(db, 'users', user.uid));

  // Delete Firebase Auth user (throws auth/requires-recent-login if session is stale)
  await deleteUser(user);
}
